import React, { createRef, useEffect, useState } from 'react';
import Geosuggest from 'react-geosuggest';
import ChevronIcon from '_icons/ChevronIcon';
import SearchIcon from '_icons/SearchIcon';
import CloseIcon from '_icons/CloseIcon';
import AddressManual from './Manual';
import CustomItem from './CustomItem';
import './Address.scss';
import { Stack, Text, cn } from 'shared-components';
import { useSelector, useDispatch } from 'react-redux';
import { getFormSyncErrors, getFormMeta, touch } from 'redux-form';

const Address = ({
  showDefaultManualAddress,
  address: _address,
  change,
  ownerIdx,
  autoFocus = true,
  getManualAddressText,
  toggleManualAddressClassName,
  geoSuggestClassName,
  geoSuggestInputClassName,
  variant,
  showLabels = false,

  addressStreetProps,
  addressSuiteProps,
  addressCityProps,
  addressStateProps,
  addressPostalCodeProps,

  addressColumnContainerProps,
  addressContainerProps,
}) => {
  const idx = ownerIdx || false;
  const addressKey = idx ? `owner_${idx}` : `business`;
  const addressType = idx ? 'Home' : 'Business';
  const dispatch = useDispatch();
  const formErrors = useSelector(getFormSyncErrors('application')) || {};
  const formMeta = useSelector(getFormMeta('application')) || {};
  const [manualAddress, setManualAddress] = useState(showDefaultManualAddress || false); //existingAddress()
  const [addressFound, setAddressFound] = useState(false);
  const [address, setAddress] = useState(_address);
  const showAddressLookup = manualAddress ? 'hide' : 'show';
  const forceShow = addressFound ? 'force-show' : '';
  const _geoSuggest = createRef();

  useEffect(() => {
    if (!window.google) {
      setManualAddress(true);
    }
    // set the input box if we've preloaded an address
    const { street, city, state } = _address;
    let streetCheck = street || '';

    try {
      if (streetCheck.length > 0 && _geoSuggest) {
        const preloadAddress = `${street}, ${city}, ${state}, USA`;
        _geoSuggest.current.update(preloadAddress);
      }
    } catch (error) {
      // console.log('geo-suggest',error);
    }
  }, []);

  const onSuggestSelect = (suggest) => {
    if (!suggest?.gmaps) {
      return;
    }

    const components = suggest.gmaps.address_components;

    const address = {
      street: (
        findAddressComponent(components, 'street_number', 'short_name') +
        ' ' +
        findAddressComponent(components, 'route', 'short_name')
      ).trim(),
      city: findAddressComponent(components, 'locality', 'short_name').trim(),
      state: findAddressComponent(components, 'administrative_area_level_1', 'short_name').trim(),
      zip: findAddressComponent(components, 'postal_code', 'short_name').trim(),
    };
    setAddressFound(1);
    setAddress(address);

    // update redux-form store with address info
    change(`${addressKey}_street`, address.street);
    change(`${addressKey}_suite`, address.suite);
    change(`${addressKey}_city`, address.city);
    change(`${addressKey}_state`, address.state);
    change(`${addressKey}_zip`, address.zip);

    // mark fields as touched so validation errors show immediately (consistent with shared-components shouldValidate: true)
    dispatch(
      touch('application', `${addressKey}_street`, `${addressKey}_city`, `${addressKey}_state`, `${addressKey}_zip`)
    );
  };

  const findAddressComponent = (components, type, version) => {
    // this address throws error: 5675 Broadway, Bronx, NY, USA
    try {
      const address = components.find(function (component) {
        return component.types.indexOf(type) !== -1;
      });

      return address[version];
    } catch (error) {
      if (type === 'locality') {
        return findAddressComponent(components, 'sublocality', version);
      }
      if (type === 'administrative_area_level_2' && addressType === 'Business') {
        return ''; // need to trigger requried validation for country on biz addresses
      }
      return '';
    }
  };

  const toggleManualAddress = () => {
    setManualAddress(!manualAddress);
  };

  const existingAddress = () => {
    if (_address.street.length > 0 && _address.city.length > 0 && _address.state.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const clearSearch = () => {
    //reset addressFound
    setAddressFound(false);

    // clear manual address fields
    change(`${addressKey}_street`, '');
    change(`${addressKey}_suite`, '');
    change(`${addressKey}_city`, '');
    change(`${addressKey}_state`, '');
    change(`${addressKey}_zip`, '');

    // clear geosuggest box and then focus it
    _geoSuggest.current?.clear();
    _geoSuggest.current?.focus();
  };

  const customSuggestItem = (suggest) => {
    const address = suggest.label.split(',');

    return <CustomItem street={address[0]} city={address[1]} state={address[2]} />;
  };
  const baseClasses =
    'w-full border-[1px] rounded-[10px] border-charcoal text-charcoal placeholder-charcoal text-left bg-spark font-normal text-[18px] px-[20px] leading-normal h-[50px] font-condensed italic focus:text-charcoal focus:font-standard focus:bg-dawn focus:rounded-[100px] focus:not-italic invalid:placeholder-zest invalid:focus:placeholder-charcoal invalid:text-zest invalid:bg-dawn transition-border-radius duration-500 ease-in-out';
  const activeClasses = 'font-standard bg-dawn rounded-[100px] not-italic';
  const completedClasses = 'font-standard !bg-dawn rounded-[10px] not-italic';
  const transparentBlackVariantClasses =
    'min-h-[60px] p-[20px] text-[16px] font-standard bg-transparent rounded-[5px] placeholder-charcoal2 disabled:border-charcoal2 text-charcoal not-italic disabled:bg-dawn focus:bg-transparent invalid:text-charcoal invalid:border-zest invalid:placeholder-charcoal2 data-[invalid=true]:text-charcoal data-[invalid=true]:border-zest data-[invalid=true]:placeholder-charcoal2';

  const addressLabel = addressType === 'Business' ? 'BUSINESS ADDRESS*' : 'HOME ADDRESS*';

  return (
    <>
      {showLabels && !manualAddress && (
        <div className="w-full max-w-[368px] mx-auto -mb-[6px]">
          <span className="block font-condensed text-[14px] text-charcoal leading-[1.2]">{addressLabel}</span>
        </div>
      )}
      <Geosuggest
        data-testid="address-search"
        ref={_geoSuggest}
        value=""
        placeholder={`${addressType} Address*`}
        onSuggestSelect={onSuggestSelect}
        readOnly={manualAddress}
        className={cn(`${showAddressLookup} w-full max-w-[368px] m-0 col-span-full`, geoSuggestClassName)}
        inputClassName={cn(baseClasses, geoSuggestInputClassName, {
          [activeClasses]: false,
          [completedClasses]: address && address.street && address.city && address.state && address.zip,
          [transparentBlackVariantClasses]: variant === 'transparent-black',
          [completedClasses]:
            address &&
            address.street &&
            address.city &&
            address.state &&
            address.zip &&
            variant === 'transparent-black',
        })}
        country="us"
        renderSuggestItem={customSuggestItem}
        autoFocus={autoFocus}
      />
      {!manualAddress && (
        <AddressFieldErrors formErrors={formErrors} formMeta={formMeta} addressKey={addressKey} variant={variant} />
      )}
      <AddressManual
        variant={variant}
        address={address}
        addressKey={addressKey}
        addressType={addressType}
        manualAddress={manualAddress}
        toggleManualAddress={toggleManualAddress}
        toggleManualAddressClassName={toggleManualAddressClassName}
        getManualAddressText={getManualAddressText}
        showLabels={showLabels}
        addressStreetProps={addressStreetProps}
        addressContainerProps={addressContainerProps}
        addressColumnContainerProps={addressColumnContainerProps}
        addressSuiteProps={addressSuiteProps}
        addressCityProps={addressCityProps}
        addressStateProps={addressStateProps}
        addressPostalCodeProps={addressPostalCodeProps}
      />
    </>
  );
};

const ADDRESS_FIELD_LABELS = {
  street: 'Street',
  city: 'City',
  state: 'State',
  zip: 'Zip code',
};

const AddressFieldErrors = ({ formErrors, formMeta, addressKey, variant }) => {
  const fieldKeys = ['street', 'city', 'state', 'zip'];
  const visibleErrors = fieldKeys
    .map((key) => {
      const fieldName = `${addressKey}_${key}`;
      const error = formErrors[fieldName];
      const meta = formMeta[fieldName];
      if (error && meta?.touched) {
        return `${ADDRESS_FIELD_LABELS[key]}: ${error}`;
      }
      return null;
    })
    .filter(Boolean);

  if (visibleErrors.length === 0) return null;

  // The `transparent-black` variant is used by the Volt-style BCM wizard
  // and needs each error on its own line with some top margin. All other
  // flows keep the original inline layout unchanged.
  const containerClass =
    variant === 'transparent-black'
      ? 'error-text max-w-[368px] mx-auto text-center w-full flex flex-col gap-[2px] mt-[8px]'
      : 'error-text max-w-[368px] mx-auto text-center w-full align-center justify-center justify-items-center';

  return (
    <div className={containerClass}>
      {visibleErrors.map((msg, i) => (
        <Text key={i} variant="error">
          {msg}
        </Text>
      ))}
    </div>
  );
};

export default Address;
